import { graphql } from 'react-relay';
import { useCallback, useContext } from 'react';

import { isCommentNotFoundError } from '@confluence/comments-util/entry-points/errorUtils.ts';
// eslint-disable-next-line no-restricted-imports -- deprecated - use RouteState instead
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { usePageContentId } from '@confluence/page-context';
import { getLogger } from '@confluence/logger';
import { expVal } from '@confluence/feature-experiments';
import { useCommentById } from '@confluence/comments-data-manager/entry-points/useCommentById.ts';
import { buildDefaultCommentsContentStatusFilters } from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import { GraphQLContentStatus } from './__types__/useCommentQueryParamsCommentLocationQuery.ts';
import type {
	useCommentQueryParamsCommentLocationQuery as useCommentQueryParamsCommentLocationQuery$data,
	useCommentQueryParamsCommentLocationQueryVariables as useCommentQueryParamsCommentLocationQuery$variables,
} from './__types__/useCommentQueryParamsCommentLocationQuery.ts';
const useCommentQueryParamsCommentLocationQuery = graphql`
	query useCommentQueryParamsCommentLocationQuery(
		$commentId: ID
		$contentStatus: [GraphQLContentStatus]
	) {
		comments(commentId: $commentId, contentStatus: $contentStatus) {
			nodes {
				container {
					id
				}
				location {
					__typename
					type
				}
				id
			}
		}
	}
`;
type InlineCommentQueryParams = {
	queryArchivedContent?: boolean;
};

export type CommentQueryParams = {
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
	location?: 'FOOTER' | 'INLINE';
	queryArchivedContent?: boolean;
};

export type CommentQueryReturnValues = {
	focusedCommentId?: string;
	editCommentId?: string;
	replyToCommentId?: string;
	shouldOpenNewCommentEditor: boolean;
	shouldMentionInNewCommentEditor: boolean;
	removeCommentQueryParams: () => void;
	setFocusedCommentQueryParam: (commentId: string) => void;
	isCommentNotFound: boolean;
};

type UseCommentQueryParamsProps = (input?: CommentQueryParams) => CommentQueryReturnValues;
type UseInlineCommentQueryParamsProps = (
	input?: InlineCommentQueryParams,
) => CommentQueryReturnValues;

const logger = getLogger('comment-query-params');

export const useCommentQueryParams: UseCommentQueryParamsProps = ({
	location,
	queryArchivedContent = false,
} = {}) => {
	const { getQueryParams, setQueryParams } = useContext(RoutesContext);
	const [pageId] = usePageContentId();
	const pageMode = useGetPageMode();

	const {
		focusedCommentId,
		editComment,
		commentId,
		replyToComment,
		showCommentArea,
		shouldMention,
		unreadCommentMark,
	} = getQueryParams();

	const targetCommentId = (focusedCommentId || replyToComment || (editComment && commentId)) as
		| string
		| undefined;
	const shouldSkipQuery = !targetCommentId || !location;

	// NEW FLOW: Use useCommentById when experiment is enabled
	const { comment, error: commentError } = useCommentById({
		commentId: targetCommentId || '',
		contentId: pageId || '',
		contentStatus: buildDefaultCommentsContentStatusFilters(pageMode),
		options: {
			skip: shouldSkipQuery || !expVal('cc_comments_new_comments_data_manager', 'isEnabled', false),
		},
	});

	// OLD FLOW: Direct GraphQL query (fallback when experiment is disabled)
	const { data: legacyData, error: legacyError } = useQueryInterop<
		useCommentQueryParamsCommentLocationQuery$data,
		useCommentQueryParamsCommentLocationQuery$variables
	>(useCommentQueryParamsCommentLocationQuery, {
		variables: {
			commentId: targetCommentId,
			contentStatus: [
				queryArchivedContent ? GraphQLContentStatus.ARCHIVED : GraphQLContentStatus.DRAFT,
				GraphQLContentStatus.CURRENT,
			],
		},
		skip: shouldSkipQuery || expVal('cc_comments_new_comments_data_manager', 'isEnabled', false),
	});
	if (legacyError) {
		logger.error`Error: Unable to query comment location for ${commentId}`;
	}

	// Determine error state - use new flow error or old flow error
	const error = expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
		? commentError
		: legacyError;

	// Note that this will detect missing inline and footer comments because the query will find
	// either type of comment - so if the comment doesn't exist, we will get the "not found" error
	// In the new flow, useCommentById returns an error when the comment isn't found after loading completes
	const isCommentNotFound = error ? isCommentNotFoundError(error.message || String(error)) : false;

	let focusedId: string | undefined;
	let editCommentId: string | undefined;
	let replyToCommentId: string | undefined;

	// Extract location and container ID from either new or old flow
	const commentLocation = expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
		? comment?.location?.type
		: legacyData?.comments?.nodes?.[0]?.location?.type;
	// Extract container ID from either new or old flow
	let commentContainerId: string | null | undefined;
	if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
		if (comment && 'container' in comment) {
			// Comment replies coming from the new manager don't have a container
			commentContainerId = comment.container?.id;
		} else {
			commentContainerId = undefined;
		}
	} else {
		commentContainerId = legacyData?.comments?.nodes?.[0]?.container?.id;
	}

	// Only return comment IDs for relevant location; If no location is given, return them all
	const shouldReturnIds =
		(!location || location === commentLocation) &&
		(pageId === commentContainerId ||
			(commentContainerId === undefined &&
				expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)));

	if (shouldReturnIds) {
		/* A focused comment query param just uses "focusedCommentId={COMMENT_ID}" */
		focusedId = focusedCommentId ? (focusedCommentId as string) : undefined;

		/* An edit comment query param uses a combination of "editComment=true&commentId={COMMENT_ID}" */
		editCommentId =
			editComment && editComment === 'true' && commentId ? (commentId as string) : undefined;

		/* A reply to comment query param just uses "replyToComment={COMMENT_ID}" */
		replyToCommentId = replyToComment ? (replyToComment as string) : undefined;
	}

	/* PAGE COMMENTS ONLY: A new comment uses the "showCommentArea=true" query param */
	const shouldOpenNewCommentEditor = showCommentArea === 'true';

	/* A mention comment uses the "shouldMention=true" query param */
	const shouldMentionInNewCommentEditor =
		shouldMention === 'true' && replyToCommentId !== undefined;

	const removeCommentQueryParamsFunction = () => {
		let commentTypeKey: string = '',
			commentIdKey: string = '',
			commentMentionKey: string = '';
		if (focusedId) {
			commentTypeKey = 'focusedCommentId';
		} else if (shouldMentionInNewCommentEditor) {
			commentTypeKey = 'replyToComment';
			commentMentionKey = 'shouldMention';
		} else if (replyToCommentId) {
			commentTypeKey = 'replyToComment';
		} else if (editCommentId) {
			commentTypeKey = 'editComment';
			commentIdKey = 'commentId';
		} else if (shouldOpenNewCommentEditor) {
			commentTypeKey = 'showCommentArea';
		}

		if (commentTypeKey || commentIdKey || commentMentionKey || unreadCommentMark) {
			const params = getQueryParams();
			setQueryParams({
				...params,
				[commentTypeKey]: undefined,
				[commentIdKey]: undefined,
				[commentMentionKey]: undefined,
				unreadCommentMark: undefined,
			});
		}
	};

	const removeCommentQueryParams = useCallback(removeCommentQueryParamsFunction, [
		getQueryParams,
		setQueryParams,
		unreadCommentMark,
		editCommentId,
		replyToCommentId,
		shouldOpenNewCommentEditor,
		shouldMentionInNewCommentEditor,
		focusedId,
	]);

	const setFocusedCommentQueryParam = (commentId: string) => {
		const params = getQueryParams();
		setQueryParams({
			...params,
			focusedCommentId: commentId,
		});
	};

	return {
		focusedCommentId: focusedId,
		editCommentId,
		replyToCommentId,
		shouldOpenNewCommentEditor,
		shouldMentionInNewCommentEditor,
		unreadCommentMark,
		removeCommentQueryParams,
		setFocusedCommentQueryParam,
		isCommentNotFound,
	};
};

export const useInlineCommentQueryParams: UseInlineCommentQueryParamsProps = ({
	queryArchivedContent = false,
} = {}) => useCommentQueryParams({ location: 'INLINE', queryArchivedContent });

export const useFooterCommentQueryParams = (): CommentQueryReturnValues =>
	useCommentQueryParams({ location: 'FOOTER' });
